import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, CheckCircle2, BarChart3, Users, Target, TrendingUp, Cpu, 
  Shield, Factory, Truck, Building2, Heart, GraduationCap, HardHat, 
  HandHeart, ChevronRight, Globe, FileText, Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BackNavigation from "@/components/BackNavigation";

export default function ObservatoryLanding() {
  const steps = [
    { number: "01", title: "Complete Diagnostic", description: "Answer 30 research-grade questions across five workforce intelligence dimensions." },
    { number: "02", title: "Receive WII Score", description: "Get your Workforce Intelligence Index instantly with detailed breakdown." },
    { number: "03", title: "Compare to GWII", description: "See how your organization compares to industry and global averages." },
    { number: "04", title: "Review Insights", description: "Receive AI-powered interpretation and targeted recommendations." },
    { number: "05", title: "Improve Strategy", description: "Use insights to strengthen workforce planning and development." }
  ];

  const dimensions = [
    { icon: Users, title: "Workforce Trust", description: "Leadership transparency, communication, and employee confidence in organizational decisions." },
    { icon: Target, title: "Organizational Alignment", description: "Strategic clarity, cross-departmental coordination, and mission-driven operations." },
    { icon: TrendingUp, title: "Workforce Readiness", description: "Skills capability, training effectiveness, and adaptability to change." },
    { icon: Cpu, title: "AI Workforce Capability", description: "AI literacy, technology investment, and strategic preparedness for AI integration." },
    { icon: Shield, title: "Organizational Resilience", description: "Change management, adaptive capacity, and workforce transformation readiness." }
  ];

  const industries = [
    { icon: Factory, name: "Manufacturing" },
    { icon: Truck, name: "Logistics" },
    { icon: Shield, name: "Security" },
    { icon: Heart, name: "Healthcare" },
    { icon: Cpu, name: "Technology" },
    { icon: Building2, name: "Government" },
    { icon: GraduationCap, name: "Education" },
    { icon: HardHat, name: "Construction" },
    { icon: HandHeart, name: "Nonprofit" }
  ];

  const publications = [
    { icon: Globe, title: "Annual Global Workforce Intelligence Report", description: "Comprehensive yearly analysis of global workforce metrics and trends." },
    { icon: Calendar, title: "Quarterly Workforce Intelligence Briefing", description: "Quarterly updates on industry trends and workforce intelligence developments." },
    { icon: FileText, title: "Monthly Workforce Intelligence Signals", description: "Monthly insights on emerging patterns and workforce indicators." }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="observatory-landing">
      {/* Back Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackNavigation fallbackPath="/observatories" fallbackLabel="Observatories" />
      </div>

      {/* Hero Section */}
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <motion.div 
              className="lg:col-span-7"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-[#4B5563]" />
                <span className="text-sm font-medium text-[#4B5563] uppercase tracking-wider">Workforce Intelligence Research</span>
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-3" style={{ color: '#991B1B' }} data-testid="observatory-title">
                Zodiac Workforce Intelligence Institute
              </h1>
              
              <p className="font-heading text-2xl sm:text-3xl font-medium mb-6" style={{ color: '#991B1B' }}>
                Workforce Intelligence Observatory
              </p>
              
              <p className="text-lg text-[#4B5563] leading-relaxed mb-8 max-w-2xl" data-testid="observatory-description">
                Advancing the science of workforce intelligence through research, diagnostics, benchmarking, and global workforce analytics.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/juswork/observatory/dashboard" data-testid="explore-cta">
                  <Button size="lg" className="bg-[#1E3A8A] text-white hover:bg-[#1E40AF] h-12 px-8 text-base">
                    Explore the Observatory
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/juswork/observatory/survey" data-testid="diagnostic-cta">
                  <Button size="lg" variant="outline" className="h-12 px-8 text-base border-[#E5E7EB] hover:bg-[#F8FAFC]">
                    Take the Workforce Intelligence Diagnostic
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div 
              className="lg:col-span-5"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="bg-[#1E3A8A] text-white border-0 shadow-2xl">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-2">Global Workforce Intelligence Index</p>
                    <p className="font-mono text-6xl font-bold">6.59</p>
                    <p className="text-white/60 text-sm mt-1">GWII Average</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 border-t border-white/10 pt-6">
                    <div className="text-center">
                      <p className="font-mono text-2xl font-bold text-blue-200">6.8</p>
                      <p className="text-white/60 text-xs">Trust</p>
                    </div>
                    <div className="text-center">
                      <p className="font-mono text-2xl font-bold text-blue-200">6.6</p>
                      <p className="text-white/60 text-xs">Alignment</p>
                    </div>
                    <div className="text-center">
                      <p className="font-mono text-2xl font-bold text-blue-200">6.7</p>
                      <p className="text-white/60 text-xs">Readiness</p>
                    </div>
                    <div className="text-center">
                      <p className="font-mono text-2xl font-bold text-blue-200">6.5</p>
                      <p className="text-white/60 text-xs">AI Capability</p>
                    </div>
                  </div>
                  <div className="text-center border-t border-white/10 pt-4 mt-4">
                    <p className="font-mono text-2xl font-bold text-blue-200">6.6</p>
                    <p className="text-white/60 text-xs">Resilience</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is the Observatory */}
      <section className="py-16 lg:py-24 bg-white" data-testid="what-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              What is the Observatory?
            </h2>
            <p className="text-lg text-[#4B5563] max-w-3xl mx-auto">
              The Workforce Intelligence Observatory is the research engine of the Zodiac Workforce Intelligence Institute, measuring critical dimensions of organizational workforce capability through a structured diagnostic instrument.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dimensions.map((dimension, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB] hover:shadow-lg transition-shadow duration-300" data-testid={`dimension-card-${index}`}>
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center mb-4">
                      <dimension.icon className="w-6 h-6 text-[#1E3A8A]" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold mb-2" style={{ color: '#991B1B' }}>{dimension.title}</h3>
                    <p className="text-sm text-[#1E3A8A]">{dimension.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GWII Section */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]" data-testid="gwii-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6" style={{ color: '#991B1B' }}>
                Global Workforce Intelligence Index
              </h2>
              <p className="text-lg text-[#4B5563] mb-6">
                The GWII aggregates workforce intelligence data across industries and regions to provide a comprehensive view of global workforce capability.
              </p>
              <ul className="space-y-4">
                {[
                  "Industry GWII — Sector-specific workforce intelligence benchmarks",
                  "Regional GWII — Geographic workforce intelligence analysis",
                  "Index Trends — Trust, alignment, readiness, AI capability, resilience patterns",
                  "Predictive Insights — AI-powered workforce intelligence forecasting"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#1E3A8A] mt-0.5 flex-shrink-0" />
                    <span className="text-[#4B5563]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: "30", label: "Diagnostic Questions" },
                { value: "5", label: "Intelligence Indices" },
                { value: "10", label: "Industry Sectors" },
                { value: "6", label: "Global Regions" }
              ].map((stat, index) => (
                <Card key={index} className="border-[#E5E7EB]">
                  <CardContent className="p-6 text-center">
                    <p className="font-mono text-3xl font-bold text-[#1E3A8A]">{stat.value}</p>
                    <p className="text-sm text-[#4B5563] mt-1">{stat.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 lg:py-24 bg-white" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              How It Works
            </h2>
            <p className="text-lg text-[#4B5563]">
              Complete the diagnostic in 5-7 minutes and receive comprehensive results.
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-4">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <Card className="h-full border-[#E5E7EB]" data-testid={`step-card-${index}`}>
                  <CardContent className="p-5">
                    <span className="font-mono text-3xl font-bold text-[#E5E7EB]">{step.number}</span>
                    <h3 className="font-heading text-base font-semibold mt-3 mb-2" style={{ color: '#991B1B' }}>{step.title}</h3>
                    <p className="text-xs text-[#1E3A8A]">{step.description}</p>
                  </CardContent>
                </Card>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-4 h-4 text-[#E5E7EB]" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries - Blue accent */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Industry Benchmarks
            </h2>
            <p className="text-lg text-[#4B5563]">
              Compare your organization against sector-specific workforce intelligence data.
            </p>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-4">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <Card className="border-[#E5E7EB] hover:shadow-md transition-shadow" data-testid={`industry-card-${index}`}>
                  <CardContent className="p-4 text-center">
                    <industry.icon className="w-6 h-6 mx-auto mb-2 text-[#1E3A8A]" />
                    <p className="text-xs text-[#4B5563] font-medium leading-tight">{industry.name}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Publications */}
      <section className="py-16 lg:py-24 bg-white" data-testid="publications-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Research Publications
            </h2>
            <p className="text-lg text-[#4B5563]">
              Access workforce intelligence research and insights.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {publications.map((pub, index) => (
              <Card key={index} className="border-[#E5E7EB] hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-[#991B1B]/10 flex items-center justify-center mb-4">
                    <pub.icon className="w-6 h-6 text-[#991B1B]" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold mb-2" style={{ color: '#991B1B' }}>{pub.title}</h3>
                  <p className="text-sm text-[#1E3A8A]">{pub.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Take the Workforce Intelligence Diagnostic
            </h2>
            <p className="text-lg text-[#4B5563] mb-8 max-w-2xl mx-auto">
              Contribute to global workforce intelligence research and receive immediate insights for your organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/juswork/observatory/survey" data-testid="final-cta">
                <Button size="lg" className="bg-[#991B1B] text-white hover:bg-[#7F1D1D] h-12 px-8 text-base">
                  Take the Diagnostic
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/juswork/observatory/dashboard">
                <Button size="lg" variant="outline" className="h-12 px-8 text-base border-[#E5E7EB]">
                  <BarChart3 className="mr-2 w-5 h-5" />
                  View Dashboard
                </Button>
              </Link>
              <Link to="/juswork/observatory/reports">
                <Button size="lg" variant="outline" className="h-12 px-8 text-base border-[#E5E7EB]">
                  View Reports
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
